import moment from 'moment-mini'
import { BehaviorSubject, combineLatest, Observable, of, pipe } from 'rxjs'
import {
  catchError,
  filter,
  first,
  map,
  startWith,
  shareReplay,
  switchMap,
  take,
  timeout
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Booking } from 'src/app/features/booking/booking'
import { bookingActions } from 'src/app/features/booking/booking.actions'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { ParticipationType } from 'src/app/features/booking/participation/participation'
import { configurationOptionSelectors } from 'src/app/features/configuration-option/configuration-option.selectors'
import { Product } from 'src/app/features/product/product'
import { productActions } from 'src/app/features/product/product.actions'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { persistentUserRoleSelectors } from 'src/app/features/role/user-role/persistent-user-role/persistent-user-role.selector'
import { UserRole } from 'src/app/features/role/user-role/user-role'
import { Tag } from 'src/app/features/tag/tag'
import { tagActions } from 'src/app/features/tag/tag.actions'
import { tagSelectors } from 'src/app/features/tag/tag.selectors'
import {
  CancellationPolicy,
  RelatedProductGroup
} from 'src/app/shared/interfaces'
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  NgForm,
  Validators
} from '@angular/forms'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { environment } from '../../../../../environments/environment'
import { ConfigurationService } from '../../../../shared/services/configuration.service'
import { BookingItem } from '../../../booking/booking-item/booking-item'
import { bookingItemActions } from '../../../booking/booking-item/booking-item.actions'
import { bookingItemSelectors } from '../../../booking/booking-item/booking-item.selectors'
import { participationSelectors } from '../../../booking/participation/participation.selectors'
import { saleSelectors } from '../../sale.selectors'
import { SaleItemPurchaseParamsFormComponent } from '../form-purchase-params/form.component'
import { SaleItemParticipantsFormComponent } from '../participants-form/participants-form.component'
import { SaleItem } from '../sale-item'
import { saleItemActions } from '../sale-item.actions'
import { saleItemSelectors } from '../sale-item.selectors'
import { SaleItemService } from '../sale-item.service'
import {
  ParticipantLimits,
  ParticipationService
} from 'src/app/features/booking/participation/participation.service'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { Member } from 'src/app/features/member/member'
import { BsModalService } from 'ngx-bootstrap/modal'
import { DmsModalComponent } from 'src/app/shared/components/dms-modal/show.component'
import { TranslateService } from '@ngx-translate/core'
import {
  ProductGroup,
  ProductGroupType
} from 'src/app/features/product/product-group/product-group'
import { productGroupSelectors } from 'src/app/features/product/product-group/product-group.selectors'
import { productGroupActions } from 'src/app/features/product/product-group/product-group.actions'
import { ProductGroupsProductService } from 'src/app/features/product/product-group/product-groups-product.service'

@UntilDestroy()
@Component({
  selector: 'sale-item-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class SaleItemFormComponent implements OnInit, AfterViewInit {
  @Output() onSubmit = new EventEmitter<any>()
  @Output() onSubmitBulk = new EventEmitter<any>()
  @Output() onSubmitDelete = new EventEmitter<any>()
  @Output() haveVisibleFather = new EventEmitter<any>()
  @Output() onBulkEditFieldsChanges = new EventEmitter<any>()
  @Output() closeBulkModal = new EventEmitter<any>()
  @Input() saleItem: Partial<SaleItem>
  @Input() saleItemsOnBulkActions: Partial<SaleItem[]>
  @Input() showSubmitButton: boolean = true
  @Input() linkedProductId: number
  @Input() isSalePaid: boolean

  @ViewChild('formRef') formRef: NgForm

  @ViewChild(SaleItemPurchaseParamsFormComponent)
  saleItemPurchaseParamsFormComponent: SaleItemPurchaseParamsFormComponent

  @ViewChild(SaleItemParticipantsFormComponent)
  saleItemParticipantsFormComponent: SaleItemParticipantsFormComponent

  public products$: Observable<Product[]>
  public productResources$: Observable<any>
  public selectedProduct$: Observable<Product>
  public selectedLanguage$: Observable<string>
  public productEntities$ = this.store.select(productSelectors.selectEntities)
  public bookingEntities$ = this.store.select(bookingSelectors.selectEntities)
  public participantsSales$: Observable<any>
  public tags$: Observable<Tag[]>
  public tags: Tag[] = []
  public filteredTags: Tag[] = []
  public tagsInProduct: any[] = []
  public saleItemParticipants$: Observable<any[]>
  public form: UntypedFormGroup
  public cancelationForm: UntypedFormGroup
  public cancellationPolicies$: Observable<CancellationPolicy[]>
  public hasDeletedRows$: Observable<boolean>
  public hasQuantityReduced$: Observable<boolean>
  public showItem$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  )
  public hideCancelationForm: boolean = true
  public isLoading$: Observable<boolean>
  public showCalendar: boolean = true
  public usesCalendar: boolean = false
  public saleBookings$: Observable<Booking[]> = of([])
  public saleItemBookingItems$: Observable<BookingItem[]>
  public isBookableProductForm$: Observable<boolean>
  public saleModuleConfig = environment.moduleConfigs.saleModule
  public isLoadingBookings$: Observable<boolean>
  public currentUserRole$: Observable<UserRole>
  public categories$: Observable<any>
  private saleId$: Observable<number> = null
  private currentUserRole: UserRole
  public members$: Observable<Member[]>
  public participationType = ParticipationType
  public saveButtonAvailable: boolean = false
  public bulkEditFieldsChanges = {
    tags: false,
    quantity: false,
    startDate: false,
    startTime: false,
    endTime: false,
    customerCategory: false,
    bookableProductId: false
  }
  public cancellationPoliciesAvailableBySaleItem$: Observable<
    CancellationPolicy[]
  >
  private productGroups$: Observable<ProductGroup[]>
  public relatedProductGroups$: Observable<RelatedProductGroup[]>
  constructor(
    private modalService: BsModalService,
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    public participationService: ParticipationService,
    private store: Store<AppState>,
    public configurationService: ConfigurationService,
    private actions$: Actions,
    private notificationService: NotificationService,
    public saleItemService: SaleItemService,
    private productGroupsProductService: ProductGroupsProductService
  ) {}

  public ngOnInit() {
    this.store.dispatch(tagActions.fetchMany({}))
    this.store.dispatch(
      productGroupActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: { type: ProductGroupType.CANCELATION_POLICY_GROUP }
        })
      })
    )

    this.productGroups$ = this.store.select(productGroupSelectors.selectAll)
    this.members$ = this.store.select(memberSelectors.selectAll)

    // Fetch related product groups from API and subscribe to trigger the call
    this.relatedProductGroups$ = this.productGroupsProductService
      .getRelatedProductGroups(this.saleItem?.productId)
      .pipe(
        catchError((error) => {
          console.error('Error fetching related product groups:', error)
          return of([])
        }),
        shareReplay(1)
      )

    this.saleId$ = this.store
      .select(saleSelectors.selectIdRouteParam)
      .pipe(map((saleId) => saleId))
    this.store.dispatch(bookingActions.actionFinishedReset())
    this.store
      .select(bookingSelectors.selectActionFinished)
      .pipe(
        first(
          (itemAction) =>
            itemAction !== null &&
            itemAction.action == bookingActions.getAllMany.type
        )
      )
      .subscribe((itemAction) => {
        this.showItem$.next(true)
      })
    if (this.saleItem?.bookingId) {
      this.store.dispatch(
        bookingActions.getAllMany({
          params: RequestQueryBuilder.create({
            search: {
              id: this.saleItem?.bookingId
            },
            join: [
              { field: 'participations' },
              { field: 'product' },
              { field: 'product.translations' },
              { field: 'product.location' },
              { field: 'product.parentBookableProducts' },
              { field: 'linkedProduct' },
              { field: 'linkedProduct.translations' }
            ]
          })
        })
      )
    } else {
      this.store.dispatch(
        bookingActions.getAllMany({
          params: RequestQueryBuilder.create({
            search: {
              'participations.saleItemId': this.saleItem?.id
            },
            join: [
              { field: 'participations' },
              { field: 'product' },
              { field: 'product.translations' },
              { field: 'product.location' },
              { field: 'product.parentBookableProducts' },
              { field: 'linkedProduct' },
              { field: 'linkedProduct.translations' }
            ]
          })
        })
      )
    }

    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            $or: [
              { id: this.saleItem?.params?.bookableProductId },
              { id: this.saleItem?.productId }
            ]
          },
          join: [
            { field: 'parentBookableProducts' },
            { field: 'childProducts' },
            { field: 'tags' }
          ]
        })
      })
    )

    this.isLoadingBookings$ = this.store.select(
      bookingSelectors.selectIsLoading
    )

    this.isLoading$ = this.store.select(saleItemSelectors.selectIsLoading)

    this.cancellationPolicies$ = this.store
      .select(configurationOptionSelectors.selectByKey, 'cancellation_policies')
      .pipe(
        filter((x) => Boolean(x)),
        map((configOption) => JSON.parse(configOption.value))
      )
    if (this.saleModuleConfig.cancelationPolicy) {
      this.cancelationForm = this.fb.group({
        cancelationPolicyUuid: [null, []]
      })
    }

    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )
    this.currentUserRole$ = this.store.select(
      persistentUserRoleSelectors.selectSelectedUserRole
    )

    this.currentUserRole$
      .pipe(untilDestroyed(this))
      .subscribe(
        (currentUserRole) =>
          (this.currentUserRole =
            currentUserRole && currentUserRole.siteId
              ? currentUserRole
              : { ...currentUserRole, siteId: environment.defaultSiteId })
      )

    this.categories$ = this.selectedLanguage$.pipe(
      switchMap((language) => {
        return this.store.select(configurationOptionSelectors.selectAll).pipe(
          map((categories) => {
            let categoriesOptions = []
            for (let category = 0; category < 10; category++) {
              const categoryDescription = categories.find(
                (cat) =>
                  cat.key === `${language}_category${category}desc` &&
                  (cat.siteId === this.currentUserRole?.siteId ||
                    !cat.siteId) &&
                  cat.value?.length
              )
              categoryDescription
                ? categoriesOptions.push(categoryDescription)
                : null
            }
            let mappedCategeries = JSON.parse(JSON.stringify(categoriesOptions))
            mappedCategeries.map((item) => {
              if (Number(item.siteId) != 0 && !item.siteId) {
                item.siteId = this.currentUserRole.siteId
              }
            })
            categoriesOptions = JSON.parse(JSON.stringify(mappedCategeries))
            return categoriesOptions
          }),
          map((configOptions) => {
            let nCategories = environment.moduleConfigs.hasExtendedCategories
              ? 10
              : 4
            return configOptions
              .filter(
                (c, i) =>
                  (c.siteId == this.currentUserRole?.siteId &&
                    i <= nCategories) ||
                  (!c.siteId && i <= nCategories)
              )
              .map((option) => option.key)
              .map((key) => key.split(language + '_').pop())
          })
        )
      })
    )

    this.form = this.fb.group({
      productId: [null, []],
      quantity: [1, []],
      customerCategory: [null, []],
      claimed: [null, []],
      bookingItemsId: [null, []],
      bookableProductId: [null],
      tags: [null, []],
      startDate: [null, []],
      startTime: [null, []],
      endTime: [null, []],
      // used to check when editing the form for booking items
      selectedBookingItemsTime: [null, []],
      quantityToReduce: [
        0,
        [Validators.min(0), Validators.max(this.saleItem?.quantity ?? 0)]
      ]
    })

    this.form.get('quantity').valueChanges.subscribe((value) => {
      if (
        value !== null &&
        value !== undefined &&
        value != this.saleItem?.quantity
      ) {
        this.form.markAsTouched()
      }
    })

    if (this.saleItem?.params) {
      this.form.patchValue({
        bookableProductId: Number(this.saleItem.params.bookableProductId),
        startDate: this.saleItem.params.startDate,
        startTime: this.saleItem.params.startDate
          ? moment(this.saleItem.params.startDate).toDate()
          : null,
        endTime: this.saleItem.params.endDate
          ? moment(this.saleItem.params.endDate).toDate()
          : null
      })
    }

    this.selectedProduct$ = this.form.controls.productId.valueChanges.pipe(
      switchMap((productId) =>
        this.store.select(productSelectors.selectById, productId)
      )
    )
    this.selectedProduct$.pipe(untilDestroyed(this)).subscribe((pr) => {
      if (!pr?.allowAsLinkedProduct && !pr?.bookable) {
        let bookings
        let productEntities
        this.store
          .select(bookingSelectors.selectAll)
          .pipe(take(1))
          .subscribe((bk) => (bookings = bk))
        this.store
          .select(productSelectors.selectEntities)
          .pipe(take(1))
          .subscribe((pr) => {
            let productsObject = {}
            Object.values(pr)
              .filter((product) => product.isActive)
              .map((productsActive) => {
                productsObject[productsActive.id] = pr[productsActive.id]
              })

            productEntities = productsObject
          })

        const productParentBookableResources =
          productEntities[this.saleItem.productId]

        const productResources =
          productParentBookableResources?.parentBookableProducts
            ?.filter((productId) => productEntities[productId]?.isActive)
            .map((parentBookableId) => productEntities[parentBookableId])

        this.productResources$ = of(productResources)
      } else {
        this.productResources$ = this.store
          .select(productSelectors.selectAll)
          .pipe(
            map((products) =>
              products.filter((product) => product.bookable && product.isActive)
            )
          )
      }

      combineLatest([
        this.store.select(participationSelectors.selectAll),
        this.store.select(bookingSelectors.selectEntities),
        this.store.select(productSelectors.selectEntities)
      ])
        .pipe(untilDestroyed(this))
        .subscribe(([participations, bookingEntities, productEntities]) => {
          const saleItemPartiipations = participations.filter(
            (p) => p.saleItemId == this.saleItem.id
          )

          const saleItemResources = saleItemPartiipations
            .map((participation) => {
              const booking = bookingEntities[participation.bookingId]
              if (!booking) {
                return null
              }

              return booking.productId ?? null
            })
            .filter((resource) => Boolean(resource))

          const uniqueResources = [...new Set(saleItemResources)]
          if (
            productEntities[this.saleItem.productId]?.bookable &&
            productEntities[this.saleItem.productId]?.allowAsLinkedProduct
          ) {
            this.form.patchValue({
              bookableProductId: uniqueResources
            })
          }
        })
    })
    this.products$ = this.store
      .select(productSelectors.selectAll)
      // only show non bookable products
      .pipe(map((products) => products.filter((p) => !p.bookable)))

    combineLatest([
      this.form.controls.bookableProductId.valueChanges,
      this.store.select(settingSelectors.selectById, 'language'),
      this.store.select(productSelectors.selectEntities)
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([resourceId, language, productEntities]) => {
        const product = productEntities[this.form.controls.productId.value]
        const bookableProduct = productEntities[resourceId]

        this.filteredTags = this.tags.filter(
          (t) =>
            product?.tags?.includes(t.id) ||
            bookableProduct?.tags?.includes(t.id)
        )
      })

    combineLatest([
      this.store.select(tagSelectors.selectAll),
      this.store.select(settingSelectors.selectById, 'language'),
      this.store.select(productSelectors.selectEntities)
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([tags, language, productEntities]) => {
        this.tags = tags.map((tag) => {
          return {
            ...tag,
            description: tag[`description_${language}`]
          }
        })

        const product = productEntities[this.form.controls.productId.value]
        const bookableProduct =
          productEntities[this.form.controls.bookableProductId.value]

        this.tagsInProduct = this.saleItem.tags
          ? this.tags
              .filter((t) => this.saleItem?.tags?.includes(t.id))
              .map((tag) => {
                return {
                  ...tag,
                  description: tag[`description_${language}`]
                }
              })
          : this.tags
              .filter((t) => product?.tags?.includes(t.id))
              .map((tag) => {
                return {
                  ...tag,
                  description: tag[`description_${language}`]
                }
              })

        this.filteredTags =
          this.tags.filter((t) => bookableProduct?.tags?.includes(t.id))
            .length > 0
            ? this.tags
                .filter(
                  (t) =>
                    product?.tags?.includes(t.id) ||
                    bookableProduct?.tags?.includes(t.id)
                )
                .map((tag) => {
                  return {
                    ...tag,
                    description: tag[`description_${language}`]
                  }
                })
            : this.tags.map((tag) => {
                return {
                  ...tag,
                  description: tag[`description_${language}`]
                }
              })
      })

    this.saleItemBookingItems$ = this.store
      .select(bookingItemSelectors.selectAll)
      .pipe(
        map((bookingItems) => {
          return bookingItems.filter((bookingItem) => {
            return (
              bookingItem.saleItemId === this.saleItem.id &&
              Boolean(bookingItem.id)
            )
          })
        })
      )
    this.saleBookings$ = combineLatest([
      this.store.select(bookingSelectors.selectAll),
      this.store.select(saleSelectors.selectById, this.saleItem.saleId),
      this.store.select(participationSelectors.selectEntities),
      this.saleItemBookingItems$
    ]).pipe(
      map(([bookings, sale, participationEntities, saleItemBookingItems]) => {
        const usedBookingIds = saleItemBookingItems.map(
          (bookingItem) => bookingItem.bookingId
        )
        const saleItemsIds = sale?.saleItems as number[]
        return bookings
          .filter((booking) => !usedBookingIds.includes(booking.id))
          .filter((booking) => Boolean(booking?.participations))
          .filter((booking) => {
            const bookingParticipations = booking?.participations?.map(
              (pId) => {
                return participationEntities[pId]
              }
            )
            return bookingParticipations?.some(
              (p) => p && saleItemsIds?.includes(p?.saleItemId)
            )
          })
      })
    )

    this.isBookableProductForm$ = this.store
      .select(participationSelectors.selectAll)
      .pipe(
        map((participations) => {
          return Boolean(
            participations.find((p) => p.saleItemId == this.saleItem.id)
          )
        })
      )
    if (this.saleItemsOnBulkActions) {
      this.form.controls['quantity'].patchValue(
        this.saleItemsOnBulkActions[0].quantity
      )
    }
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((changes) => {
      this.saveButtonAvailable = true
      this.form.markAsTouched()
    })

    const originalQuantity = this.saleItem.quantity

    if (this.isSalePaid === true) {
      this.form.get('customerCategory')?.disable()
    }

    this.form
      .get('quantity')
      .valueChanges.pipe()
      .subscribe((newQuantity) => {
        if (newQuantity < 0) {
          this.form.get('quantity').setValue(0, { emitEvent: false })
          return
        }

        if (this.isSalePaid === true && newQuantity > originalQuantity) {
          this.form
            .get('quantity')
            .setValue(originalQuantity, { emitEvent: false })
        }
      })

    this.cancellationPoliciesAvailableBySaleItem$ = combineLatest([
      this.cancellationPolicies$,
      this.productGroups$,
      this.relatedProductGroups$
    ]).pipe(
      map(([cancellationPolicies, productGroups, relatedProductGroups]) => {
        const cancellationPolicyGroups = productGroups?.filter(
          (pg) => pg.type == ProductGroupType.CANCELATION_POLICY_GROUP
        )
        if (!cancellationPolicyGroups.length) {
          return []
        }
        const relatedProductGroupIds = relatedProductGroups.map(
          (rpg) => rpg.productGroupId
        )
        if (!relatedProductGroupIds.length) {
          return []
        }
        const applicableProductGroupIds = cancellationPolicyGroups
          .filter((cpg) => relatedProductGroupIds.includes(cpg.id))
          .map((cpg) => cpg.id)
        if (!applicableProductGroupIds.length) {
          return []
        }
        const filteredPolicies = cancellationPolicies.filter((policy) => {
          return policy.values.some((value) => {
            return value.productGroupIds.some((pgId) =>
              applicableProductGroupIds.includes(pgId)
            )
          })
        })

        return filteredPolicies
      })
    )
  }

  compareWith(o1: any, o2: any) {
    if (o1.description == o2.description && o1.id == o2.id) return true
    else return false
  }
  public ngAfterViewInit() {
    this.saleItemPurchaseParamsFormComponent.form.removeControl(
      'primaryPurchaseMessage'
    )
    this.saleItemPurchaseParamsFormComponent.form.removeControl(
      'secondaryPurchaseMessage'
    )

    this.hasQuantityReduced$ = combineLatest([
      this.form.controls.quantity.valueChanges.pipe(
        startWith(this.form.controls.quantity.value)
      ),
      this.form.controls.quantityToReduce.valueChanges.pipe(
        startWith(this.form.controls.quantityToReduce.value)
      )
    ]).pipe(
      filter(
        ([quantity, quantityToReduce]) =>
          (quantity != null && quantity !== '') ||
          (quantityToReduce != null && quantityToReduce !== '')
      ),
      map(
        ([quantity, quantityToReduce]) =>
          Number(quantity) < this.saleItem.quantity || quantityToReduce > 0
      )
    )
  }

  public getParticipantLimits$(): Observable<ParticipantLimits> {
    return combineLatest([
      of(this.saleItem),
      this.store.select(productSelectors.selectEntities),
      this.store.select(bookingSelectors.selectEntities),
      this.store.select(participationSelectors.selectAll)
    ]).pipe(
      take(1),
      map(([saleItem, productEntities, bookingEntities, participations]) => {
        const booking = saleItem.bookingId
          ? bookingEntities[saleItem.bookingId]
          : [
                ...new Set(
                  participations
                    .filter((item) => item.saleItemId == saleItem.id)
                    .map((item) => item.bookingId)
                )
              ].length > 0
            ? bookingEntities[
                [
                  ...new Set(
                    participations
                      .filter((item) => item.saleItemId == saleItem.id)
                      .map((item) => item.bookingId)
                  )
                ][0]
              ]
            : null
        if (!booking) {
          return {
            maxGuestInvites: 0,
            maxOtherInvites: 0,
            maxMemberInvites: 0,
            maxParticipants: 0,
            minParticipants: 0,
            spotsLeft: 0
          }
        }

        return this.participationService.calculateParticipantLimits(
          productEntities[booking?.productId],
          productEntities[booking?.linkedProductId],
          booking?.maxParticipants
        )
      })
    )
  }

  public onFormSubmit(): void {
    if (!this.form.valid) {
      return
    }

    if (this.saleItemsOnBulkActions) {
      let cancelationPolicyUuid
      if (
        this.saleModuleConfig.cancelationPolicy &&
        this.cancelationForm.controls.cancelationPolicyUuid.value
      ) {
        cancelationPolicyUuid =
          this.cancelationForm.controls.cancelationPolicyUuid.value
      }
      let someFiledNeedBeChanged = false
      Object.values(this.bulkEditFieldsChanges).forEach((item) => {
        if (item) {
          someFiledNeedBeChanged = true
        }
      })
      if (!someFiledNeedBeChanged) {
        const modalRef = this.modalService.show(DmsModalComponent, {
          class: 'modal-sm modal-dialog-centered modal-dialog-scrollable ',
          backdrop: 'static',
          initialState: {
            detailText: 'saleItemModule.bulkEditDetailText'
          }
        })
        modalRef.onHide.pipe(take(1)).subscribe(() => {
          if (modalRef.content.action == 'yes') {
            this.closeBulkModal.emit(true)
          }
          if (modalRef.content.action == 'no') {
            this.bulkEditFieldsChanges = {
              tags: false,
              quantity: false,
              startDate: false,
              startTime: false,
              endTime: false,
              customerCategory: false,
              bookableProductId: false
            }
            modalRef.hide()
          }
        })
        return
      } else {
        this.translate
          .get('saleItemModule.bulkEditDetailText1')
          .pipe(take(1))
          .subscribe((text) => {
            let selectedFields = Object.keys(this.bulkEditFieldsChanges)
              .map((key) => {
                if (this.bulkEditFieldsChanges[key]) {
                  return key
                }
              })
              .filter((item) => Boolean(item))
              .toString()
            text = text.replace('${0}', JSON.stringify(selectedFields))
            const modalRef = this.modalService.show(DmsModalComponent, {
              class: 'modal-sm modal-dialog-centered modal-dialog-scrollable ',
              backdrop: 'static',
              initialState: {
                detailText: text
              }
            })
            modalRef.onHide.pipe(take(1)).subscribe((event) => {
              if (modalRef.content.action == 'yes') {
                this.onBulkEditFieldsChanges.emit(this.bulkEditFieldsChanges)
                setTimeout(() => {
                  this.onSubmitBulk.emit({
                    ...this.form.value,
                    cancelationPolicyUuid
                  })
                }, 100)

                return
              } else {
                this.bulkEditFieldsChanges = {
                  tags: false,
                  quantity: false,
                  startDate: false,
                  startTime: false,
                  endTime: false,
                  customerCategory: false,
                  bookableProductId: false
                }

                modalRef.hide()
                return
              }
            })
          })

        return
      }
    }
    const { bookableProductId, ...saleItem } = this.form.value
    if (this.form.controls.customerCategory.disabled) {
      saleItem.customerCategory = this.form.controls.customerCategory.value
    }

    if (saleItem.params && bookableProductId) {
      if (Array.isArray(bookableProductId)) {
        saleItem.params.bookableProductId = bookableProductId[0]
      } else {
        saleItem.params.bookableProductId = bookableProductId
      }
      saleItem.params.bookableLinkedProductId = saleItem.productId
    }

    if (saleItem.params) {
      const params = saleItem.params ?? {}
      // add default start time
      const startTime = this.form.get('startTime').value
      const endTime = this.form.get('endTime').value
      const startDate = this.form.get('startDate').value
      if (startTime && startDate) {
        const startTimeMoment = moment(startTime)
        params.startDate = moment(startDate).set({
          hour: startTimeMoment.get('hour'),
          minute: startTimeMoment.get('minute'),
          second: startTimeMoment.get('second')
        })
      }
      if (endTime && startDate) {
        const endTimeMoment = moment(endTime)
        params.endDate = moment(startDate).set({
          hour: endTimeMoment.get('hour'),
          minute: endTimeMoment.get('minute'),
          second: endTimeMoment.get('second')
        })
      }
    }

    if (
      this.saleModuleConfig.cancelationPolicy &&
      this.cancelationForm.controls.cancelationPolicyUuid.value
    ) {
      saleItem.cancelationPolicyUuid =
        this.cancelationForm.controls.cancelationPolicyUuid.value
    }
    combineLatest([
      this.store.select(saleItemSelectors.selectEntities),
      this.productEntities$
    ])
      .pipe(take(1))
      .subscribe(([saleItemEntities, productEntities]) => {
        const product = productEntities[this.saleItem.productId]
        const isOtherProduct =
          product && !product?.bookable && !product?.allowAsLinkedProduct

        let saleItemWithParams = {
          ...saleItem,
          params: {}
        }

        // * I need a fresh instance from the store
        // * because now the user can change the participantcount
        // * with the saleItemAdjustParticipants component
        const sItem = saleItemEntities[this.saleItem.id]

        if (sItem?.params?.invitedGuests) {
          saleItemWithParams.params['invitedGuests'] =
            sItem.params?.invitedGuests
        }
        if (sItem?.params?.invitedOthers) {
          saleItemWithParams.params['invitedOthers'] =
            sItem.params?.invitedOthers
        }
        if (sItem?.params?.invitedMemberEmails) {
          saleItemWithParams.params['invitedMemberEmails'] =
            sItem.params?.invitedMemberEmails
        }

        if (isOtherProduct) {
          saleItemWithParams['params']['bookableProductId'] = bookableProductId
          saleItemWithParams['params']['endDate'] = moment(saleItem.startDate)
            .set('h', moment(saleItem.endTime).get('h'))
            .set('m', moment(saleItem.endTime).get('m'))
            .set('s', moment(saleItem.endTime).get('s'))
            .set('millisecond', 0)
            .toISOString()
          saleItemWithParams['params']['startDate'] = moment(saleItem.startDate)
            .set('h', moment(saleItem.startTime).get('h'))
            .set('m', moment(saleItem.startTime).get('m'))
            .set('s', moment(saleItem.startTime).get('s'))
            .set('millisecond', 0)
            .toISOString()
        }

        if (
          'quantityToReduce' in saleItemWithParams &&
          !!saleItemWithParams.quantityToReduce
        ) {
          saleItemWithParams['quantity'] =
            saleItemWithParams.quantity - saleItemWithParams.quantityToReduce
          delete saleItemWithParams.quantityToReduce
        }

        if ('claimed' in saleItemWithParams && saleItemWithParams.claimed) {
          const modalRef = this.modalService.show(DmsModalComponent, {
            class: 'modal-sm modal-dialog-centered modal-dialog-scrollable ',
            backdrop: 'static',
            initialState: {
              detailText: 'saleModule.saleItemUsedAdjustment',
              buttons: ['general.yes', 'general.cancel'],
              valuesToEmitOnClickButton: ['yes', 'cancel']
            }
          })
          modalRef.onHide.pipe(take(1)).subscribe(() => {
            if (modalRef.content.action === 'yes') {
              this.onSubmit.emit(saleItemWithParams)
            }
          })
        } else {
          this.onSubmit.emit(saleItemWithParams)
        }
      })
  }

  public onPreconfirmDelete(): void {
    if (this.saleModuleConfig.cancelationPolicy) {
      this.hideCancelationForm = false
    } else {
      this.onSubmitDelete.emit()
    }
  }

  public onDelete() {
    if (this.saleModuleConfig.cancelationPolicy) {
      this.onSubmitDelete.emit({
        cancelationPolicyUuid:
          this.cancelationForm.controls.cancelationPolicyUuid.value
      })
    }
  }
  public isFatherVisible(visibleFather) {
    this.haveVisibleFather.emit(visibleFather)
  }
  public attemptSubmit(): void {
    Object.keys(this.form.controls).map((controlName) =>
      this.form.controls[controlName].markAsDirty()
    )

    this.formRef.ngSubmit.emit(undefined)
  }

  linkToBooking(event: any) {
    event.preventDefault()

    this.bookingEntities$.pipe(take(1)).subscribe((bookingEntities) => {
      const bookingId = this.form.controls.bookingItemsId.value as number
      if (!bookingId) {
        return
      }

      const bookingItem: Partial<BookingItem> = {
        bookingId: bookingId,
        saleItemId: this.saleItem.id,
        quantity: this.saleItem.quantity,
        startDate: bookingEntities[bookingId]?.startDate,
        endDate: bookingEntities[bookingId]?.endDate,
        saleId: this.saleItem.saleId,
        productId: bookingEntities[bookingId]?.productId
      }

      this.store.dispatch(
        bookingItemActions.createMany({
          entities: [bookingItem]
        })
      )
      this.form.controls.bookingItemsId.setValue(null)
    })

    this.actions$
      .pipe(
        ofType(bookingItemActions.upsertMany),
        take(1),
        timeout(1000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        // backend not sending back the created bookingItems with ids
        this.store.dispatch(
          saleItemActions.fetchOneById({
            id: this.saleItem.id,
            params: RequestQueryBuilder.create({
              join: ['bookingItems']
            })
          })
        )
      })
  }
  public existCurrentparticipant(booking) {
    return (
      booking.hasOwnProperty('currentParticipantCount') &&
      booking.currentParticipantCount >= 0
    )
  }
  removeBookingItem(item: BookingItem) {
    if (!item.id) {
      return
    }

    this.store.dispatch(bookingItemActions.deleteOne({ id: item.id }))
  }

  editingBookingItemId: number = null
  editBookingItem(bookingItem: BookingItem) {
    this.editingBookingItemId = bookingItem.id
    this.form.controls.selectedBookingItemsTime.setValue(
      new Date(bookingItem.startDate as string)
    )
  }

  cancelEditBookingItem() {
    this.editingBookingItemId = null
  }

  updateBookingItem(data: BookingItem) {
    const startDate = this.form.get('selectedBookingItemsTime').value

    this.store.dispatch(
      bookingItemActions.updateOneById({
        id: data.id,
        params: {
          startDate
        }
      })
    )
    this.editingBookingItemId = null
  }

  disabledHours(date: Date): () => number[] {
    if (!date) {
      return null
    }
    return () => {
      const hours = Array(24)
        .fill(0)
        .map((_, i) => i)
      return hours.filter((h) => h <= date.getHours())
    }
  }

  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          },
          join: [
            { field: 'translations' },
            { field: 'childProducts' },
            { field: 'parentBookableProducts' },
            { field: 'tags' }
          ]
        })
      })
    )
  }

  public getParticipationsCount$(): Observable<any> {
    return combineLatest([
      this.store.select(participationSelectors.selectByFilter, {
        field: 'saleItemId',
        value: this.saleItem.id
      }),
      this.store.select(saleItemSelectors.selectPartials)
    ]).pipe(
      untilDestroyed(this),
      map(([participations, saleItemPartials]): any => {
        const partialFilterds = saleItemPartials
          .filter((p) => p.id == this.saleItem.id)
          .map((partial) => partial.params)

        if (!participations?.length && !partialFilterds?.length) {
          return {
            participantsGuests: 0,
            participantsMembers: 0,
            participantsOthers: 0,
            total: 0
          }
        }

        let participantsGuests = 0
        let participantsMembers = 0
        let participantsOthers = 0

        if (partialFilterds?.length) {
          partialFilterds.map((partial) => {
            participantsGuests += partial.invitedGuests?.length
            participantsOthers += partial.invitedOthers?.length
            participantsMembers += partial.invitedMemberEmails?.length
          })
        }

        if (participations?.length) {
          participantsGuests += participations.filter(
            (participation) => participation.type == ParticipationType.GUEST
          )?.length

          participantsOthers += participations.filter(
            (participation) => participation.type == ParticipationType.OTHER
          )?.length

          participantsMembers += participations.filter(
            (participation) => participation.type == ParticipationType.MEMBER
          )?.length
        }

        return {
          participantsGuests,
          participantsMembers,
          participantsOthers,
          total: participantsGuests + participantsMembers + participantsOthers
        }
      })
    )
  }

  public patchValueForm(field, value: boolean) {
    let patchValue = {}

    patchValue[field] = value
    this.form.patchValue(patchValue)

    if (field == 'claimed' && this.saleItem?.id) {
      this.actions$
        .pipe(
          ofType(saleItemActions.upsertOne, saleItemActions.upsertMany),
          take(1),
          timeout(1000),
          catchError((error) => of(null))
        )
        .subscribe((action) => {
          if (action) {
            this.notificationService.emit('alerts.success', 'success')
          }
        })

      this.store.dispatch(
        saleItemActions.updateOneById({
          id: this.saleItem.id,
          params: {
            claimed: value
          }
        })
      )
    }
  }

  public getCategoryValue(categoryDescription: string): number {
    const regex = /\d+/
    return Number(categoryDescription.match(regex)?.[0])
  }
  public onCheckRow(field) {
    this.bulkEditFieldsChanges[field] = !this.bulkEditFieldsChanges[field]
  }
}
